import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';
import AdminLayout from '../../components/layout/AdminLayout';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

type Usuario = {
  id: string;
  nombre: string;
  email: string;
  tipo_membresia: string;
  estado: 'activo' | 'inactivo' | 'vencido';
  avatar?: string;
};

type ProgresoData = {
  id: string;
  usuario_id: string;
  fecha: string;
  peso: number;
  grasa_corporal: number;
  masa_muscular: number;
  notas: string;
};

type ProgresoReporte = {
  id: string;
  usuario_id: string;
  fecha_medicion: string;
  fuente: 'fitmao' | 'manual' | 'otro' | string;
  fuente_url: string;
  mime_type: string | null;
  file_size: number | null;
  resumen: Record<string, unknown> | null;
  created_at: string;
};

type EntrenamientoEjercicio = {
  id: string;
  entrenamiento_id: string;
  ejercicio_id: number | null;
  nombre: string;
  categoria: string | null;
  series: number | null;
  repeticiones: number | null;
  descanso: number | null;
  peso_plan: number | null;
  peso_usado: number | null;
  completado: boolean;
};

type Entrenamiento = {
  id: string;
  usuario_id: string;
  fecha: string; // YYYY-MM-DD
  sesion_id: string;
  rutina_id: string | null;
  entrenamiento_ejercicios?: EntrenamientoEjercicio[];
};

type RutinaPersonalizada = {
  id: string;
  nombre: string;
  descripcion: string | null;
  activa: boolean;
  ejercicios: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

export default function ProgresoPage() {
  const { sucursalActiva } = useSucursal();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [selectedUsuario, setSelectedUsuario] = useState<Usuario | null>(null);
  const [progreso, setProgreso] = useState<ProgresoData[]>([]);
  const [reportes, setReportes] = useState<ProgresoReporte[]>([]);
  const [entrenamientos, setEntrenamientos] = useState<Entrenamiento[]>([]);
  const [rutinasPersonalizadas, setRutinasPersonalizadas] = useState<RutinaPersonalizada[]>([]);
  const [rutinaNombreById, setRutinaNombreById] = useState<Record<string, string>>({});
  const [loadingEntrenamientos, setLoadingEntrenamientos] = useState(false);
  const [loadingRutinasPersonalizadas, setLoadingRutinasPersonalizadas] = useState(false);
  const [loadingReportes, setLoadingReportes] = useState(false);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importingReporte, setImportingReporte] = useState(false);
  const [openingReporteId, setOpeningReporteId] = useState<string | null>(null);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [reportesError, setReportesError] = useState<string | null>(null);
  const [reportesNotice, setReportesNotice] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0],
    peso: '',
    grasa_corporal: '',
    masa_muscular: '',
    notas: '',
  });

  const [importForm, setImportForm] = useState({
    source_url: '',
    fecha_medicion: new Date().toISOString().split('T')[0],
    peso: '',
    grasa_corporal: '',
    masa_muscular: '',
    imc: '',
    pbf: '',
    grasa_visceral: '',
    tmb: '',
    notas: '',
  });

  const parseISODateLocal = (iso: string) => new Date(`${iso}T00:00:00`);
  const parseOptionalNumber = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getValidAccessToken = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error('Sesion expirada. Vuelve a iniciar sesion.');
    }

    const expiresAtMs = (session.expires_at || 0) * 1000;
    if (expiresAtMs > 0 && expiresAtMs - Date.now() < 60_000) {
      const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !refreshed.session?.access_token) {
        throw new Error('Sesion expirada. Vuelve a iniciar sesion.');
      }
      return refreshed.session.access_token;
    }

    return session.access_token;
  };

  const callEdgeFunction = async <T,>(functionName: string, payload: Record<string, unknown>): Promise<T> => {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      throw new Error('Configuracion de Supabase incompleta en frontend.');
    }

    const accessToken = await getValidAccessToken();
    const response = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const body = await response.json().catch(() => ({} as any));
    if (!response.ok) {
      if (body?.error && body?.details) {
        throw new Error(`${body.error}: ${body.details}`);
      }
      if (body?.error) {
        throw new Error(String(body.error));
      }
      throw new Error(`Edge Function error (${response.status})`);
    }

    return body as T;
  };

  const openUrlInNewTab = (url: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    fetchUsuarios();
  }, [sucursalActiva]);

  useEffect(() => {
    if (selectedUsuario) {
      setReportesError(null);
      setReportesNotice(null);
      fetchProgreso(selectedUsuario.id);
      fetchProgresoReportes(selectedUsuario.id);
      fetchEntrenamientos(selectedUsuario.id);
      fetchRutinasPersonalizadas(selectedUsuario.id);
    } else {
      setReportes([]);
      setRutinasPersonalizadas([]);
    }
  }, [selectedUsuario]);

  const fetchUsuarios = async () => {
    if (!sucursalActiva?.id) {
      setUsuarios([]);
      setSelectedUsuario(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const query = supabase
        .from('usuarios')
        .select('id, nombre, email, tipo_membresia, estado')
        .eq('sucursal_id', sucursalActiva.id)
        .order('nombre', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;

      const usuariosConAvatar = (data || []).map(u => ({
        ...u,
        avatar: u.nombre.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
      }));

      setUsuarios(usuariosConAvatar);
      
      if (usuariosConAvatar.length > 0 && !selectedUsuario) {
        setSelectedUsuario(usuariosConAvatar[0]);
      }
    } catch (err) {
      console.error('Error fetching usuarios:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgreso = async (usuarioId: string) => {
    try {
      const { data, error } = await supabase
        .from('progreso')
        .select('*')
        .eq('usuario_id', usuarioId)
        .order('fecha', { ascending: false });

      if (error) throw error;

      setProgreso(data || []);
    } catch (err) {
      console.error('Error fetching progreso:', err);
    }
  };

  const fetchProgresoReportes = async (usuarioId: string) => {
    try {
      setLoadingReportes(true);
      const { data, error } = await supabase
        .from('progreso_reportes')
        .select('id,usuario_id,fecha_medicion,fuente,fuente_url,mime_type,file_size,resumen,created_at')
        .eq('usuario_id', usuarioId)
        .order('fecha_medicion', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setReportes((data as ProgresoReporte[]) || []);
    } catch (err: any) {
      console.error('Error fetching progreso reportes:', err);
      setReportes([]);
      setReportesError(err?.message || 'No se pudieron cargar los reportes');
    } finally {
      setLoadingReportes(false);
    }
  };

  const fetchEntrenamientos = async (usuarioId: string) => {
    try {
      setLoadingEntrenamientos(true);
      setExpandedDay(null);

      const { data, error } = await supabase
        .from('entrenamientos')
        .select(
          `
          id,
          usuario_id,
          fecha,
          sesion_id,
          rutina_id,
          entrenamiento_ejercicios (
            id,
            entrenamiento_id,
            ejercicio_id,
            nombre,
            categoria,
            series,
            repeticiones,
            descanso,
            peso_plan,
            peso_usado,
            completado
          )
        `
        )
        .eq('usuario_id', usuarioId)
        .order('fecha', { ascending: false })
        .limit(200);

      if (error) throw error;

      const rows = (data || []) as Entrenamiento[];
      setEntrenamientos(rows);

      const rutinaIds = Array.from(
        new Set(rows.map((r) => r.rutina_id).filter((id): id is string => typeof id === 'string' && id.length > 0))
      );

      if (rutinaIds.length > 0) {
          const { data: rutData, error: rutErr } = await supabase
            .from('rutinas')
            .select('id, nombre')
            .in('id', rutinaIds as any);

        if (!rutErr) {
          const map: Record<string, string> = {};
          for (const r of rutData || []) {
            map[String((r as any).id)] = String((r as any).nombre || '');
          }
          setRutinaNombreById(map);
        }
      } else {
        setRutinaNombreById({});
      }
    } catch (err: any) {
      // If the DB isn't migrated yet, keep this section empty but don't break the page.
      console.error('Error fetching entrenamientos:', err);
      setEntrenamientos([]);
      setRutinaNombreById({});
    } finally {
      setLoadingEntrenamientos(false);
    }
  };

  const fetchRutinasPersonalizadas = async (usuarioId: string) => {
    try {
      setLoadingRutinasPersonalizadas(true);
      const { data, error } = await supabase
        .from('rutinas')
        .select('id,nombre,descripcion,activa,ejercicios,created_at,updated_at')
        .eq('creada_por_usuario', true)
        .contains('usuarios_asignados', [usuarioId])
        .order('updated_at', { ascending: false })
        .limit(30);

      if (error) throw error;
      setRutinasPersonalizadas((data as RutinaPersonalizada[]) || []);
    } catch (err) {
      console.error('Error fetching rutinas personalizadas:', err);
      setRutinasPersonalizadas([]);
    } finally {
      setLoadingRutinasPersonalizadas(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedUsuario) return;

    try {
      const { error } = await supabase
        .from('progreso')
        .insert([{
          usuario_id: selectedUsuario.id,
          fecha: formData.fecha,
          peso: parseFloat(formData.peso),
          grasa_corporal: parseFloat(formData.grasa_corporal),
          masa_muscular: parseFloat(formData.masa_muscular),
          notas: formData.notas,
        }]);

      if (error) throw error;

      setShowModal(false);
      resetForm();
      fetchProgreso(selectedUsuario.id);
    } catch (err) {
      console.error('Error saving progreso:', err);
    }
  };

  const resetImportForm = () => {
    setImportForm({
      source_url: '',
      fecha_medicion: new Date().toISOString().split('T')[0],
      peso: '',
      grasa_corporal: '',
      masa_muscular: '',
      imc: '',
      pbf: '',
      grasa_visceral: '',
      tmb: '',
      notas: '',
    });
  };

  const handleImportFitmao = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUsuario) return;

    try {
      setImportingReporte(true);
      setReportesError(null);
      setReportesNotice(null);

      const sourceUrl = importForm.source_url.trim();
      if (!sourceUrl) {
        throw new Error('Debes ingresar la URL del informe.');
      }

      const resumen: Record<string, unknown> = {};
      const imc = parseOptionalNumber(importForm.imc);
      const pbf = parseOptionalNumber(importForm.pbf);
      const visceral = parseOptionalNumber(importForm.grasa_visceral);
      const tmb = parseOptionalNumber(importForm.tmb);

      if (imc !== null) resumen.imc = imc;
      if (pbf !== null) resumen.pbf = pbf;
      if (visceral !== null) resumen.grasa_visceral = visceral;
      if (tmb !== null) resumen.tmb = tmb;

      const progresoPayload: Record<string, unknown> = {};
      const peso = parseOptionalNumber(importForm.peso);
      const grasa = parseOptionalNumber(importForm.grasa_corporal);
      const musculo = parseOptionalNumber(importForm.masa_muscular);
      const notas = importForm.notas.trim();

      if (peso !== null) progresoPayload.peso = peso;
      if (grasa !== null) progresoPayload.grasa_corporal = grasa;
      if (musculo !== null) progresoPayload.masa_muscular = musculo;
      if (notas) progresoPayload.notas = notas;

      const body: Record<string, unknown> = {
        usuario_id: selectedUsuario.id,
        source_url: sourceUrl,
        fecha_medicion: importForm.fecha_medicion,
        fuente: 'fitmao',
      };
      if (Object.keys(resumen).length > 0) body.resumen = resumen;
      if (Object.keys(progresoPayload).length > 0) body.progreso = progresoPayload;

      await callEdgeFunction('progreso-import-fitmao', body);

      setShowImportModal(false);
      resetImportForm();
      setReportesNotice('Informe importado correctamente.');
      await Promise.all([fetchProgreso(selectedUsuario.id), fetchProgresoReportes(selectedUsuario.id)]);
    } catch (err: any) {
      setReportesError(err?.message || 'No se pudo importar el informe');
    } finally {
      setImportingReporte(false);
    }
  };

  const openReporte = async (reporte: ProgresoReporte) => {
    try {
      setOpeningReporteId(reporte.id);
      setReportesError(null);
      setReportesNotice(null);

      const mime = String(reporte.mime_type || '').toLowerCase();
      const isHtmlLike = mime.includes('text/html') || mime.includes('text/plain');
      if ((reporte.fuente === 'fitmao' || isHtmlLike) && reporte.fuente_url) {
        openUrlInNewTab(reporte.fuente_url);
        return;
      }

      const data = await callEdgeFunction<{ url?: string }>('progreso-reportes-signed-url', {
          reporte_id: reporte.id,
          expires_seconds: 3600,
      });
      const signedUrl = String(data?.url || '');
      if (!signedUrl) {
        throw new Error('No se pudo generar URL firmada del informe');
      }

      openUrlInNewTab(signedUrl);
    } catch (err: any) {
      setReportesError(err?.message || 'No se pudo abrir el informe');
    } finally {
      setOpeningReporteId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      fecha: new Date().toISOString().split('T')[0],
      peso: '',
      grasa_corporal: '',
      masa_muscular: '',
      notas: '',
    });
  };

  const filteredUsuarios = usuarios.filter((usuario) =>
    usuario.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    usuario.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const entrenamientosByDay = entrenamientos.reduce((acc, e) => {
    const day = e.fecha;
    if (!acc[day]) acc[day] = [];
    acc[day].push(e);
    return acc;
  }, {} as Record<string, Entrenamiento[]>);

  const daysEntrenados = Object.keys(entrenamientosByDay).sort((a, b) => (a < b ? 1 : -1));

  const getDayStats = (day: string) => {
    const sessions = entrenamientosByDay[day] || [];
    const ejercicios = sessions.flatMap((s) => s.entrenamiento_ejercicios || []);
    const completados = ejercicios.filter((x) => x.completado);
    const tonnage = completados.reduce((sum, ex) => {
      const peso = Number(ex.peso_usado ?? ex.peso_plan ?? 0) || 0;
      const series = Number(ex.series ?? 0) || 0;
      const reps = Number(ex.repeticiones ?? 0) || 0;
      return sum + peso * series * reps;
    }, 0);
    return {
      sesiones: sessions.length,
      ejercicios: completados.length,
      tonnage,
    };
  };

  // Calcular estadísticas desde datos reales
  const entrenamientosEsteMes = (() => {
    const hoy = new Date();
    if (daysEntrenados.length > 0) {
      return daysEntrenados.filter((d) => {
        const fecha = parseISODateLocal(d);
        return fecha.getMonth() === hoy.getMonth() && fecha.getFullYear() === hoy.getFullYear();
      }).length;
    }
    // Fallback (legacy): use "progreso" entries as proxy.
    return progreso.filter((p) => {
      const fecha = parseISODateLocal(p.fecha);
      return fecha.getMonth() === hoy.getMonth() && fecha.getFullYear() === hoy.getFullYear();
    }).length;
  })();

  const ultimoPeso = progreso.length > 0 ? progreso[0].peso : 0;
  const pesoAnterior = progreso.length > 1 ? progreso[1].peso : ultimoPeso;
  const rachaActual = Math.min(entrenamientosEsteMes, 8);

  const volumenTotal = (() => {
    if (daysEntrenados.length === 0) {
      return progreso.slice(0, 4).reduce((sum, p) => sum + (p.peso * 10), 0);
    }
    const last7Days = daysEntrenados.slice(0, 7);
    return Math.round(
      last7Days.reduce((sum, d) => sum + getDayStats(d).tonnage, 0)
    );
  })();

  const progresoRutina = progreso.length > 0 ? Math.min((entrenamientosEsteMes / 20) * 100, 100) : 0;

  // Datos para gráficos (agrupados por semana del mes de referencia).
  const weekOfMonth = (date: Date) => Math.floor((date.getDate() - 1) / 7) + 1;
  const getLatestDataDate = () => {
    const latestEntrenamiento = daysEntrenados.length > 0 ? parseISODateLocal(daysEntrenados[0]) : null;
    const latestProgreso = progreso.length > 0 ? parseISODateLocal(progreso[0].fecha) : null;
    if (!latestEntrenamiento && !latestProgreso) return new Date();
    if (!latestEntrenamiento) return latestProgreso as Date;
    if (!latestProgreso) return latestEntrenamiento;
    return latestEntrenamiento > latestProgreso ? latestEntrenamiento : latestProgreso;
  };

  const referenceDate = getLatestDataDate();
  const referenceMonth = referenceDate.getMonth();
  const referenceYear = referenceDate.getFullYear();

  const volumenByWeek = new Map<number, number>();
  for (const day of daysEntrenados) {
    const fecha = parseISODateLocal(day);
    if (fecha.getMonth() !== referenceMonth || fecha.getFullYear() !== referenceYear) continue;
    const week = weekOfMonth(fecha);
    const current = volumenByWeek.get(week) || 0;
    volumenByWeek.set(week, current + getDayStats(day).tonnage);
  }

  const pesoByWeek = new Map<number, number>();
  const progresoMesOrdenado = [...progreso]
    .filter((p) => {
      const fecha = parseISODateLocal(p.fecha);
      return fecha.getMonth() === referenceMonth && fecha.getFullYear() === referenceYear;
    })
    .sort((a, b) => (a.fecha > b.fecha ? 1 : -1));
  for (const row of progresoMesOrdenado) {
    const week = weekOfMonth(parseISODateLocal(row.fecha));
    // Conserva la última medición de esa semana.
    pesoByWeek.set(week, row.peso);
  }

  const weeklyKeys = Array.from(new Set([...volumenByWeek.keys(), ...pesoByWeek.keys()])).sort((a, b) => a - b);
  const weeklySeries = weeklyKeys.map((week) => ({
    week,
    volumen: volumenByWeek.get(week) || 0,
    peso: pesoByWeek.has(week) ? (pesoByWeek.get(week) as number) : null,
  }));

  const maxVolumen = Math.max(1, ...weeklySeries.map((w) => w.volumen));
  const pesosSemanales = weeklySeries
    .map((w) => w.peso)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  const minPeso = pesosSemanales.length > 0 ? Math.min(...pesosSemanales) - 5 : 0;
  const maxPeso = pesosSemanales.length > 0 ? Math.max(...pesosSemanales) + 5 : 1;
  const rangoPeso = Math.max(1, maxPeso - minPeso);

  const getResumenLabel = (resumen: Record<string, unknown> | null) => {
    if (!resumen || typeof resumen !== 'object') return 'Sin resumen';
    const piezas: string[] = [];
    const pick = (key: string) => parseOptionalNumber(String((resumen as Record<string, unknown>)[key] ?? ''));
    const imc = pick('imc');
    const pbf = pick('pbf');
    const visceral = pick('grasa_visceral');
    const tmb = pick('tmb');

    if (imc !== null) piezas.push(`IMC ${imc.toFixed(1)}`);
    if (pbf !== null) piezas.push(`PBF ${pbf.toFixed(1)}%`);
    if (visceral !== null) piezas.push(`Visceral ${visceral.toFixed(1)}`);
    if (tmb !== null) piezas.push(`TMB ${Math.round(tmb)} kcal`);
    return piezas.length > 0 ? piezas.join(' · ') : 'Sin resumen';
  };

  const formatBytes = (value: number | null) => {
    if (!value || value <= 0) return '-';
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getArchivoLabel = (reporte: ProgresoReporte) => {
    const mime = String(reporte.mime_type || '').toLowerCase();
    if (reporte.fuente === 'fitmao' || mime.includes('text/html') || mime.includes('text/plain')) {
      return 'URL externa (FitMao)';
    }
    if (!mime) return 'Archivo';
    return mime;
  };

  const getSesionesRutina = (rutina: RutinaPersonalizada) => {
    const ejercicios = rutina.ejercicios;
    if (!ejercicios || typeof ejercicios !== 'object') return 0;
    const sesiones = (ejercicios as Record<string, unknown>).sesiones;
    return Array.isArray(sesiones) ? sesiones.length : 0;
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Progreso de Usuarios</h1>
            <p className="text-gray-600 mt-1">Seguimiento de evolución y métricas</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (!selectedUsuario) return;
                setReportesError(null);
                setReportesNotice(null);
                setShowImportModal(true);
              }}
              disabled={!selectedUsuario}
              className="bg-blue-500 text-white px-5 py-3 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-download-cloud-2-line"></i>
              Importar FitMao
            </button>
            <button
              onClick={() => setShowModal(true)}
              disabled={!selectedUsuario}
              className="bg-teal-500 text-white px-6 py-3 rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Registrar Progreso
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
                <span className="text-xl font-bold text-teal-600">{selectedUsuario?.avatar || ''}</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{selectedUsuario?.nombre || 'Sin usuario seleccionado'}</h1>
                <p className="text-sm text-gray-500">{selectedUsuario?.email || ''}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                    {selectedUsuario?.tipo_membresia || 'Sin plan'}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    selectedUsuario?.estado === 'activo' ? 'bg-green-100 text-green-700' :
                    selectedUsuario?.estado === 'vencido' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedUsuario?.estado.charAt(0).toUpperCase() + selectedUsuario?.estado.slice(1) || ''}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowUserSelector(true)}
              className="bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-user-search-line"></i>
              Cambiar Usuario
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Entrenamientos este Mes</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{entrenamientosEsteMes}</p>
                <p className="text-xs text-teal-600 mt-1">+3 vs mes anterior</p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <i className="ri-calendar-check-line text-2xl text-teal-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Racha Actual</p>
                <p className="text-3xl font-bold text-orange-600 mt-1">{rachaActual} días</p>
                <p className="text-xs text-orange-600 mt-1">Mejor racha: 15 días</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <i className="ri-fire-line text-2xl text-orange-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Volumen Total</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">{volumenTotal.toLocaleString()} kg</p>
                <p className="text-xs text-blue-600 mt-1">+12% este mes</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-bar-chart-box-line text-2xl text-blue-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Progreso Rutina</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{Math.round(progresoRutina)}%</p>
                <p className="text-xs text-green-600 mt-1">Rutina Intermedia</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-pie-chart-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Volumen de Entrenamiento Semanal</h3>
            {weeklySeries.length === 0 ? (
              <div className="text-sm text-gray-500">No hay datos semanales para el mes seleccionado.</div>
            ) : (
              <div className="space-y-4">
                {weeklySeries.map((row) => {
                  const altura = Math.min((row.volumen / maxVolumen) * 100, 100);
                  return (
                    <div key={`vol-${row.week}`}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Semana {row.week}</span>
                        <span className="font-semibold text-gray-900">{Math.round(row.volumen).toLocaleString()} kg</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-teal-500 to-teal-600 h-full rounded-full transition-all duration-500"
                          style={{ width: `${altura}%`, maxHeight: '12rem' }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Evolución del Peso</h3>
            {weeklySeries.length === 0 ? (
              <div className="text-sm text-gray-500">No hay datos semanales para el mes seleccionado.</div>
            ) : (
              <div className="space-y-4">
                {weeklySeries.map((row) => {
                  const altura = row.peso !== null ? Math.min(((row.peso - minPeso) / rangoPeso) * 100, 100) : 0;
                  return (
                    <div key={`peso-${row.week}`}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Semana {row.week}</span>
                        <span className="font-semibold text-gray-900">{row.peso !== null ? `${row.peso} kg` : '-'}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full transition-all duration-500"
                          style={{ width: `${altura}%`, maxHeight: '12rem' }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">Historial de Mediciones</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Peso</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Grasa Corporal</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Masa Muscular</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Notas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {progreso.map((registro) => (
                  <tr key={registro.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {new Date(registro.fecha).toLocaleDateString('es-CL')}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{registro.peso} kg</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{registro.grasa_corporal}%</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{registro.masa_muscular} kg</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{registro.notas || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {progreso.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-line-chart-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-4">No hay registros de progreso</p>
              <p className="text-sm text-gray-400 mt-2">Comienza registrando las primeras mediciones</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Rutinas personalizadas del usuario</h3>
              <p className="text-sm text-gray-500 mt-1">
                Rutinas creadas por el usuario en la app móvil para revisión del entrenador.
              </p>
            </div>
            <div className="text-sm text-gray-500">
              {rutinasPersonalizadas.length} rutina{rutinasPersonalizadas.length !== 1 ? 's' : ''}
            </div>
          </div>

          {loadingRutinasPersonalizadas ? (
            <div className="p-6">
              <div className="flex items-center gap-3 text-gray-600">
                <div className="w-5 h-5 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
                Cargando rutinas personalizadas...
              </div>
            </div>
          ) : rutinasPersonalizadas.length === 0 ? (
            <div className="text-center py-10">
              <i className="ri-dumbbell-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-3">No hay rutinas personalizadas para este usuario</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {rutinasPersonalizadas.map((rutina) => (
                <div key={rutina.id} className="p-6 flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900">{rutina.nombre}</p>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          rutina.activa ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {rutina.activa ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                    {rutina.descripcion && <p className="text-sm text-gray-600 mt-1">{rutina.descripcion}</p>}
                    <div className="text-xs text-gray-500 mt-2">
                      {getSesionesRutina(rutina)} sesión{getSesionesRutina(rutina) !== 1 ? 'es' : ''} ·{' '}
                      Actualizada {new Date(rutina.updated_at).toLocaleString('es-CL')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Reportes de Composicion Corporal</h3>
              <p className="text-sm text-gray-500 mt-1">Importados desde FitMao y almacenados en Supabase.</p>
            </div>
            <button
              onClick={() => {
                if (!selectedUsuario) return;
                setReportesError(null);
                setReportesNotice(null);
                setShowImportModal(true);
              }}
              disabled={!selectedUsuario}
              className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-file-add-line"></i>
              Importar reporte
            </button>
          </div>

          {reportesError && (
            <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {reportesError}
            </div>
          )}
          {reportesNotice && (
            <div className="mx-6 mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {reportesNotice}
            </div>
          )}

          {loadingReportes ? (
            <div className="p-6">
              <div className="flex items-center gap-3 text-gray-600">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                Cargando reportes...
              </div>
            </div>
          ) : reportes.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-file-chart-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-4">No hay reportes importados para este usuario</p>
              <p className="text-sm text-gray-400 mt-2">Usa "Importar reporte" para guardar el primer informe FitMao.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-y border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Fecha medicion</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Fuente</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Resumen</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Archivo</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Cargado</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reportes.map((reporte) => (
                    <tr key={reporte.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {new Date(`${reporte.fecha_medicion}T00:00:00`).toLocaleDateString('es-CL')}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {String(reporte.fuente || 'fitmao').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{getResumenLabel(reporte.resumen)}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div>{getArchivoLabel(reporte)}</div>
                        <div className="text-xs text-gray-500">{formatBytes(reporte.file_size)}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(reporte.created_at).toLocaleString('es-CL')}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => openReporte(reporte)}
                          disabled={openingReporteId === reporte.id}
                          className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap inline-flex items-center gap-2"
                        >
                          <i className={`ri-external-link-line ${openingReporteId === reporte.id ? 'animate-pulse' : ''}`}></i>
                          Ver informe
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Entrenamientos por día</h3>
              <p className="text-sm text-gray-500 mt-1">
                Sesiones y ejercicios completados (para control y análisis administrativo)
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (selectedUsuario) fetchEntrenamientos(selectedUsuario.id);
                }}
                className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center gap-2"
                disabled={!selectedUsuario || loadingEntrenamientos}
              >
                <i className={`ri-refresh-line ${loadingEntrenamientos ? 'animate-spin' : ''}`}></i>
                Actualizar
              </button>
              <div className="text-sm text-gray-500">
                {daysEntrenados.length} día{daysEntrenados.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {loadingEntrenamientos ? (
            <div className="p-6">
              <div className="flex items-center gap-3 text-gray-600">
                <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                Cargando entrenamientos...
              </div>
            </div>
          ) : daysEntrenados.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-calendar-event-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-4">Aún no hay entrenamientos registrados</p>
              <p className="text-sm text-gray-400 mt-2">
                Esta sección se alimenta cuando el usuario marca ejercicios como completados en la app (con conexión a internet).
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Si acabas de completar una sesión, presiona “Actualizar” o recarga la página.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {daysEntrenados.slice(0, 30).map((day) => {
                const stats = getDayStats(day);
                const expanded = expandedDay === day;
                return (
                  <div key={day} className="p-6">
                    <button
                      onClick={() => setExpandedDay(expanded ? null : day)}
                      className="w-full flex items-center justify-between cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                          <i className="ri-calendar-check-line text-teal-600 text-xl"></i>
                        </div>
                        <div className="text-left">
                          <div className="font-semibold text-gray-900">
                            {parseISODateLocal(day).toLocaleDateString('es-CL', {
                              weekday: 'long',
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            })}
                          </div>
                          <div className="text-sm text-gray-500">
                            {stats.sesiones} sesión{stats.sesiones !== 1 ? 'es' : ''} · {stats.ejercicios} ejercicio{stats.ejercicios !== 1 ? 's' : ''} ·{' '}
                            {Math.round(stats.tonnage).toLocaleString('es-CL')} kg
                          </div>
                        </div>
                      </div>
                      <div className="text-gray-400">
                        <i className={`ri-arrow-down-s-line text-2xl transition-transform ${expanded ? 'rotate-180' : ''}`}></i>
                      </div>
                    </button>

                    {expanded && (
                      <div className="mt-5 space-y-4">
                        {(entrenamientosByDay[day] || []).map((ses) => {
                          const ejercicios = (ses.entrenamiento_ejercicios || []).filter((x) => x.completado);
                          const rutinaName = ses.rutina_id ? rutinaNombreById[String(ses.rutina_id)] : '';
                          return (
                            <div key={ses.id} className="border border-gray-200 rounded-xl p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-semibold text-gray-900">
                                    Sesión {ses.sesion_id}
                                    {rutinaName ? ` · ${rutinaName}` : ''}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {ejercicios.length} ejercicio{ejercicios.length !== 1 ? 's' : ''} completado{ejercicios.length !== 1 ? 's' : ''}
                                  </div>
                                </div>
                              </div>

                              {ejercicios.length === 0 ? (
                                <div className="text-sm text-gray-500 mt-3">Sin ejercicios completados.</div>
                              ) : (
                                <div className="mt-3 overflow-x-auto">
                                  <table className="w-full">
                                    <thead className="bg-gray-50 border border-gray-200">
                                      <tr>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Ejercicio</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Series</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Reps</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Peso plan</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Peso usado</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 border border-gray-200">
                                      {ejercicios.map((ex) => (
                                        <tr key={ex.id} className="hover:bg-gray-50">
                                          <td className="px-3 py-2 text-sm text-gray-900">
                                            <div className="font-medium">{ex.nombre}</div>
                                            {ex.categoria && <div className="text-xs text-gray-500">{ex.categoria}</div>}
                                          </td>
                                          <td className="px-3 py-2 text-sm text-gray-900">{ex.series ?? '-'}</td>
                                          <td className="px-3 py-2 text-sm text-gray-900">{ex.repeticiones ?? '-'}</td>
                                          <td className="px-3 py-2 text-sm text-gray-900">{ex.peso_plan ?? '-'}</td>
                                          <td className="px-3 py-2 text-sm font-semibold text-teal-700">{ex.peso_usado ?? '-'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Registrar Progreso</h2>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.fecha}
                      onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Peso (kg) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      required
                      value={formData.peso}
                      onChange={(e) => setFormData({ ...formData, peso: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Grasa Corporal (%) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      required
                      value={formData.grasa_corporal}
                      onChange={(e) => setFormData({ ...formData, grasa_corporal: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Masa Muscular (kg) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      required
                      value={formData.masa_muscular}
                      onChange={(e) => setFormData({ ...formData, masa_muscular: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notas
                    </label>
                    <textarea
                      value={formData.notas}
                      onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Observaciones adicionales..."
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
                  >
                    Guardar Progreso
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Importar informe FitMao</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Usuario: <span className="font-medium text-gray-800">{selectedUsuario?.nombre || '-'}</span>
                </p>
              </div>

              <form onSubmit={handleImportFitmao} className="p-6">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">URL informe FitMao *</label>
                    <input
                      type="url"
                      required
                      value={importForm.source_url}
                      onChange={(e) => setImportForm((prev) => ({ ...prev, source_url: e.target.value }))}
                      placeholder="https://www.fitmao.com/..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fecha medicion *</label>
                      <input
                        type="date"
                        required
                        value={importForm.fecha_medicion}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, fecha_medicion: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                      Si completas mediciones abajo, tambien se registra en el historial de progreso.
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Peso (kg)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={importForm.peso}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, peso: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Grasa corporal (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={importForm.grasa_corporal}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, grasa_corporal: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Masa muscular (kg)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={importForm.masa_muscular}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, masa_muscular: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">IMC</label>
                      <input
                        type="number"
                        step="0.1"
                        value={importForm.imc}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, imc: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">PBF (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={importForm.pbf}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, pbf: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Grasa visceral</label>
                      <input
                        type="number"
                        step="0.1"
                        value={importForm.grasa_visceral}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, grasa_visceral: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">TMB (kcal)</label>
                      <input
                        type="number"
                        step="1"
                        value={importForm.tmb}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, tmb: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notas (opcional)</label>
                    <textarea
                      rows={3}
                      value={importForm.notas}
                      onChange={(e) => setImportForm((prev) => ({ ...prev, notas: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Comentario interno del informe..."
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowImportModal(false);
                      resetImportForm();
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={importingReporte}
                    className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {importingReporte && <i className="ri-loader-4-line animate-spin"></i>}
                    Importar informe
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showUserSelector && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Seleccionar Usuario</h2>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      placeholder="Buscar usuario..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredUsuarios.map((usuario) => (
                    <div
                      key={usuario.id}
                      onClick={() => {
                        setSelectedUsuario(usuario);
                        setShowUserSelector(false);
                      }}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                        <span className="text-teal-600 font-semibold text-sm">{usuario.avatar}</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{usuario.nombre}</p>
                        <p className="text-sm text-gray-500">{usuario.tipo_membresia || 'Sin plan'}</p>
                      </div>
                      <i className="ri-arrow-right-s-line text-gray-400"></i>
                    </div>
                  ))}
                </div>

                {filteredUsuarios.length === 0 && (
                  <div className="text-center py-8">
                    <i className="ri-user-line text-4xl text-gray-300"></i>
                    <p className="text-gray-500 mt-2">No se encontraron usuarios</p>
                  </div>
                )}

                <div className="mt-6">
                  <button
                    onClick={() => setShowUserSelector(false)}
                    className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
